import type { IIntentItem } from 'src/types/file';

import { useRef, useState, useEffect, useCallback } from 'react';

import { getRunStatus } from 'src/hooks/api/pipeline';

import { RunConfigStatus } from 'src/utils/enumerations';
import { FINAL_RUN_CONFIG_STATUSES } from 'src/utils/constants';
import { getRefetchFileTime, mapRunConfigStatus, isTimeTwentyMinutesOld } from 'src/utils/utils';

import { useFiles } from './useFiles';

export function useCheckRunConfigStatus() {
  const { updateRunConfigStatus } = useFiles();
  const [intentsToCheck, setIntentsToCheck] = useState<IIntentItem[]>([]);
  const timersRef = useRef<NodeJS.Timeout[]>([]);

  // Function to check and update the status of a intent run config
  const checkAndUpdateStatus = useCallback(
    async (
      intentId: string,
      runConfigId: string,
      refetchTime: number,
      lastUpdate: Date,
      lastStatus: string
    ) => {
      const timer = setTimeout(async () => {
        const { status } = await getRunStatus(runConfigId);
        if (mapRunConfigStatus(status) !== lastStatus) {
          // If the status changes, update the cache
          updateRunConfigStatus(intentId, status);
        }

        if (FINAL_RUN_CONFIG_STATUSES.includes(status) || isTimeTwentyMinutesOld(lastUpdate)) {
          // If the status is final or the status was updated 20min ago, remove the intent from the list to check
          setIntentsToCheck((prevIntentToCheck) =>
            prevIntentToCheck.filter((intent) => intent.id !== intentId)
          );
        } else {
          // If the status is not final, recursively check the status again after the refetch time
          checkAndUpdateStatus(intentId, runConfigId, refetchTime, lastUpdate, lastStatus);
        }
      }, refetchTime);
      timersRef.current.push(timer); // Store the timer reference
    },
    [updateRunConfigStatus]
  );

  const checkInProgressIntentConfigs = useCallback(
    (intents: IIntentItem[]) => {
      // Filter intents that are currently in progress and last status update happened less than 20min ago
      const inProgressIntents = intents.filter(
        (intent) =>
          intent.runConfigStatus === RunConfigStatus.InProgress &&
          !isTimeTwentyMinutesOld(intent.runConfigUpdated)
      );

      if (inProgressIntents.length >= 0) {
        inProgressIntents.forEach((intent) => {
          // If the intent is not already in the list to check, add it and start checking its status
          if (!intentsToCheck.some((intentToCheck) => intentToCheck.id === intent.id)) {
            setIntentsToCheck((prev) => [...prev, intent]);
            checkAndUpdateStatus(
              intent.id,
              intent.runConfigId,
              getRefetchFileTime(intent.audioFileSize), // Use the audio file size to determine the refetch time
              intent.runConfigUpdated,
              intent.runConfigStatus
            );
          }
        });
      } else {
        setIntentsToCheck([]); // Clear the list if no intents are being processed
      }
    },
    [checkAndUpdateStatus, intentsToCheck]
  );

  useEffect(
    () =>
      // Cleanup function to clear all timers when the component unmounts
      () => {
        timersRef.current.forEach(clearTimeout);
        timersRef.current = [];
      },
    []
  );

  return {
    checkInProgressIntentConfigs,
  };
}
